import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  type AssignmentPart,
  type AssignmentSummaryStats,
  type QuestionAnalytics,
  type SheetMarks,
  type SubmissionSummary,
} from "../api.js";

/** Teacher view: everyone's submissions for one assignment, and who is missing. */
export default function Submissions() {
  const { id } = useParams<{ id: string }>();
  const [submissions, setSubmissions] = useState<SubmissionSummary[]>([]);
  const [summary, setSummary] = useState<AssignmentSummaryStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [latestOnly, setLatestOnly] = useState(true);
  const [showMissing, setShowMissing] = useState(false);
  /** Set when this is a sheet: submissions belong to its questions, not to it. */
  const [parts, setParts] = useState<AssignmentPart[] | null>(null);
  /** The whole marking grid, fetched only once we know this is a sheet. */
  const [sheet, setSheet] = useState<SheetMarks | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .get<{ assignment: { parts: AssignmentPart[] } }>(`/api/assignments/${id}`)
      .then((r) => setParts(r.assignment.parts.length > 0 ? r.assignment.parts : null))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id || !parts) return;
    api
      .get<SheetMarks>(`/api/assignments/${id}/sheet-marks`)
      .then(setSheet)
      .catch((e) => setError(e.message));
  }, [id, parts]);

  useEffect(() => {
    if (!id) return;
    api
      .get<{ submissions: SubmissionSummary[]; summary: AssignmentSummaryStats }>(
        `/api/assignments/${id}/submissions`
      )
      .then((r) => {
        setSubmissions(r.submissions);
        setSummary(r.summary);
      })
      .catch((e) => setError(e.message));
  }, [id]);

  // Students resubmit repeatedly; showing every attempt by default buries the
  // one that matters. The list arrives newest-first, so the first hit per
  // student is their latest.
  const seen = new Set<string>();
  const rows = latestOnly
    ? submissions.filter((s) => {
        const key = s.student?.id ?? "";
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
    : submissions;

  return (
    <div className="page">
      <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>

      <div className="between" style={{ margin: "10px 0 16px" }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>
          {parts ? sheet?.sheet.title ?? "Marks" : "Submissions"}
        </h1>
        <div className="row">
          {/* Meaningless on a sheet: the grid is one cell per question, and a
              cell is already the latest. */}
          {!parts && (
            <label className="row" style={{ marginBottom: 0, width: "auto" }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={latestOnly}
                onChange={(e) => setLatestOnly(e.target.checked)}
              />
              <span style={{ marginBottom: 0 }}>Latest per student only</span>
            </label>
          )}
          {/* A plain link rather than fetch(): the browser handles the download
              and carries the session cookie with it. */}
          <a href={`/api/assignments/${id}/marks.csv`} download>
            <button>Export marks (CSV)</button>
          </a>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {parts && sheet && (
        <>
          <div className="card">
            <div className="stats">
              <Stat
                value={`${sheet.summary.completedCount}/${sheet.summary.rosterCount}`}
                label="completed the sheet"
                warn={sheet.summary.completedCount < sheet.summary.rosterCount}
              />
              <Stat value={sheet.summary.startedCount} label="started" />
              <Stat
                value={sheet.summary.average ?? "—"}
                label={`average of ${sheet.summary.sheetMaxMarks}`}
              />
              <Stat value={sheet.summary.median ?? "—"} label="median" />
              <Stat value={sheet.summary.highest ?? "—"} label="highest" />
              <Stat value={sheet.summary.lowest ?? "—"} label="lowest" />
            </div>
            <p className="muted" style={{ fontSize: 13, margin: "12px 0 0" }}>
              Each cell is that student's latest submission for the question — the one the mark
              opens, and the one the export records. A blank cell means nothing was submitted,
              which is not the same as a zero.
              {sheet.summary.awaitingReview > 0 &&
                ` ${sheet.summary.awaitingReview} submission${sheet.summary.awaitingReview === 1 ? "" : "s"} not yet reviewed by a teacher.`}
            </p>
          </div>

          <div className="card" style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Roll no.</th>
                  {parts.map((p, i) => (
                    <th key={p.id} style={{ whiteSpace: "nowrap" }}>
                      <Link to={`/assignments/${p.id}/submissions`} title={p.title}>
                        Q{i + 1}
                      </Link>
                      <span className="muted" style={{ fontWeight: 400 }}> /{p.maxMarks}</span>
                    </th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {sheet.rows.map((r) => (
                  <tr key={r.student.id}>
                    <td>{r.student.name}</td>
                    <td className="muted">{r.student.rollNumber ?? "—"}</td>
                    {r.cells.map((c) => (
                      <td key={c.partId}>
                        {c.submissionId === null ? (
                          <span className="muted">—</span>
                        ) : (
                          <Link to={`/submissions/${c.submissionId}`} title={
                            `${c.status?.toLowerCase().replace("_", " ")}${c.manual ? ", marked by a teacher" : ""}${c.late ? ", late" : ""}`
                          }>
                            {c.score ?? "?"}
                            {c.late && <span className="badge warn" style={{ marginLeft: 4 }}>late</span>}
                          </Link>
                        )}
                      </td>
                    ))}
                    <td>
                      <strong>{r.total ?? "—"}</strong>
                      <span className="muted"> / {sheet.summary.sheetMaxMarks}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {summary && !parts && (
        <>
          <div className="card">
            <div className="stats">
              <Stat
                value={`${summary.submittedCount}/${summary.rosterCount}`}
                label="submitted"
                warn={summary.notSubmitted.length > 0}
              />
              <Stat value={summary.average ?? "—"} label={`average of ${summary.maxMarks}`} />
              <Stat value={summary.median ?? "—"} label="median" />
              <Stat value={summary.highest ?? "—"} label="highest" />
              <Stat value={summary.lowest ?? "—"} label="lowest" />
              <Stat value={summary.fullMarks} label="full marks" />
              <Stat value={summary.zero} label="scored zero" />
              {summary.lateCount > 0 && <Stat value={summary.lateCount} label="late" />}
            </div>
            <p className="muted" style={{ fontSize: 13, margin: "12px 0 0" }}>
              Marks count each student's latest submission, manual where one was given — the same
              one the export records and the Review link opens.
              {summary.awaitingReview > 0 &&
                ` ${summary.awaitingReview} submission${summary.awaitingReview === 1 ? "" : "s"} not yet reviewed by a teacher.`}
            </p>
          </div>

          <QuestionAnalyticsCard id={id!} />

          {summary.notSubmitted.length > 0 && (
            <div className="card">
              <div className="between">
                <strong>Not submitted ({summary.notSubmitted.length})</strong>
                <button onClick={() => setShowMissing((v) => !v)}>
                  {showMissing ? "Hide" : "Show"}
                </button>
              </div>
              {showMissing && (
                <table style={{ marginTop: 10 }}>
                  <thead>
                    <tr><th>Roll no.</th><th>Name</th><th>Email</th><th>Batch</th></tr>
                  </thead>
                  <tbody>
                    {summary.notSubmitted.map((s) => (
                      <tr key={s.id}>
                        <td>{s.rollNumber ?? "—"}</td>
                        <td>{s.name}</td>
                        <td className="muted">{s.email}</td>
                        <td className="muted">{s.batchName ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      <div className="card" style={parts ? { display: "none" } : undefined}>
        {rows.length === 0 ? (
          <div className="muted">No submissions yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Student</th>
                <th>Roll no.</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Auto</th>
                <th>Final</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>{s.student?.name ?? "—"}</td>
                  <td className="muted">{s.student?.rollNumber ?? "—"}</td>
                  <td>
                    {new Date(s.submittedAt).toLocaleString()}{" "}
                    {s.late && <span className="badge warn">late</span>}
                  </td>
                  <td>
                    <span className={`badge ${s.status === "GRADED" ? "pass" : "fail"}`}>
                      {s.status.toLowerCase().replace("_", " ")}
                    </span>
                  </td>
                  <td>{s.autoScore ?? "—"}</td>
                  <td>
                    <strong>{s.manualScore ?? s.autoScore ?? "—"}</strong>
                    {s.manualScore !== null && <span className="muted" style={{ fontSize: 11 }}> (manual)</span>}
                  </td>
                  <td><Link to={`/submissions/${s.id}`}><button>Review</button></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * Why a question went the way it did.
 *
 * Folded away by default: a teacher opens this page to mark, and reaches for
 * the diagnosis when the marks look wrong. It is fetched on expand rather than
 * with the page, since it is the more expensive query of the two.
 */
function QuestionAnalyticsCard({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<QuestionAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || data) return;
    api
      .get<QuestionAnalytics>(`/api/assignments/${id}/analytics`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [open, data, id]);

  const peak = Math.max(1, ...(data?.distribution.map((b) => b.count) ?? [1]));
  // A case nobody passes is the signature of a wrong expected output, so it is
  // called out rather than left for the reader to spot in a column of numbers.
  const suspect = data?.testCases.filter((t) => t.ran > 0 && t.passed === 0) ?? [];

  return (
    <div className="card">
      <div className="between">
        <strong>Question analytics</strong>
        <button onClick={() => setOpen((v) => !v)}>{open ? "Hide" : "Show"}</button>
      </div>
      {open && error && <div className="error-banner" style={{ marginTop: 10 }}>{error}</div>}
      {open && data && (
        <div style={{ marginTop: 12 }}>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Over each student's latest submission — {data.summary.attemptedCount} of{" "}
            {data.summary.rosterCount} students, {data.summary.submissionCount} submissions in all.
            {data.summary.compileErrors > 0 &&
              ` ${data.summary.compileErrors} did not compile; those run no test cases at all.`}
          </p>

          {suspect.length > 0 && (
            <div className="error-banner" style={{ marginBottom: 12 }}>
              {suspect.length === 1 ? "One test case was" : `${suspect.length} test cases were`} failed
              by every student who ran {suspect.length === 1 ? "it" : "them"}:{" "}
              {suspect.map((t) => t.name).join(", ")}. Check the expected output before marking.
            </div>
          )}

          {data.testCases.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Test case</th>
                  <th>Passed</th>
                  <th>Pass rate</th>
                  <th>Mean time</th>
                  <th>Slowest</th>
                  <th>Timed out</th>
                </tr>
              </thead>
              <tbody>
                {data.testCases.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {t.name} {t.hidden && <span className="badge draft">hidden</span>}
                    </td>
                    <td>{t.passed} / {t.ran}</td>
                    <td>
                      <span className={`badge ${t.passRate === null ? "draft" : t.passRate >= 50 ? "pass" : "fail"}`}>
                        {t.passRate === null ? "—" : `${t.passRate}%`}
                      </span>
                    </td>
                    <td className="muted">{t.meanTimeMs === null ? "—" : `${t.meanTimeMs} ms`}</td>
                    <td className="muted">
                      {t.maxTimeMs === null ? "—" : `${t.maxTimeMs} ms`}
                      {/* The limit is per run, so a slowest time near it means
                          the next student's slightly slower solution fails on
                          the machine rather than on the logic. */}
                      {t.maxTimeMs !== null && t.maxTimeMs > data.assignment.timeLimitMs * 0.8 && (
                        <span className="badge warn" style={{ marginLeft: 6 }}>near limit</span>
                      )}
                    </td>
                    <td className="muted">{t.timedOut || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {data.options.length > 0 && (
            <table>
              <thead>
                <tr><th>Option</th><th>Chosen by</th></tr>
              </thead>
              <tbody>
                {data.options.map((o) => (
                  <tr key={o.id}>
                    <td>
                      {o.text} {o.correct && <span className="badge pass">correct</span>}
                    </td>
                    <td>
                      {o.chosen}
                      {data.summary.attemptedCount > 0 && (
                        <span className="muted">
                          {" "}({Math.round((o.chosen / data.summary.attemptedCount) * 100)}%)
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 16 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              MARKS DISTRIBUTION (of {data.assignment.maxMarks})
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 90 }}>
              {data.distribution.map((b) => (
                <div
                  key={b.from}
                  title={`${b.count} student${b.count === 1 ? "" : "s"} scored ${b.from}–${b.to}`}
                  style={{ flex: 1, textAlign: "center" }}
                >
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>{b.count || ""}</div>
                  <div
                    style={{
                      height: `${(b.count / peak) * 60}px`,
                      background: "var(--accent)",
                      borderRadius: "3px 3px 0 0",
                      minHeight: b.count > 0 ? 2 : 0,
                    }}
                  />
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>{b.to}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  value,
  label,
  warn,
}: {
  value: string | number;
  label: string;
  warn?: boolean;
}) {
  return (
    <div className="stat">
      <div className="value" style={warn ? { color: "var(--warn)" } : undefined}>{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}
